import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import {
  FeedbackApiLoadingService,
  type FeedbackRound,
  FeedbackRoundService,
  FeedbackRoundStatus,
  type Paginated,
} from '@brafton/feedback-api';
import { Pagination } from '@brafton/feedback-components';
import { ProjectsOverviewBanner } from '../../components/overview-banner/overview-banner';
import {
  FeedbackRoundListing,
  type RoundStatusChange,
} from '../../components/feedback-round-listing/feedback-round-listing';
import { StatusFilter, type StatusFilterValue } from '../../components/status-filter/status-filter';
import { CurrentUserService } from '../../core/current-user.service';
import {
  NewProjectDrawer,
  type NewProjectFormValue,
} from '../../components/new-project-drawer/new-project-drawer';
import {
  PROJECT_STATUS_TILES,
  type ProjectStatusCounts,
} from '../../components/overview-banner/project-status';

/** Per-status roll-up used to populate the overview banner. */
interface StatusSummary {
  status: FeedbackRoundStatus;
  total: number;
  orgIds: string[];
  failed: boolean;
}

/**
 * A page of rounds plus whether the fetch failed, so an errored load is told
 * apart from a genuinely empty account rather than both rendering as no rows.
 */
interface ListState {
  page: Paginated<FeedbackRound>;
  failed: boolean;
}

const EMPTY_STATUS_COUNTS: ProjectStatusCounts = {
  [FeedbackRoundStatus.Open]: 0,
  [FeedbackRoundStatus.Completed]: 0,
  [FeedbackRoundStatus.Closed]: 0,
  [FeedbackRoundStatus.Cancelled]: 0,
};

const EMPTY_PAGE: Paginated<FeedbackRound> = {
  page: 1,
  perPage: 0,
  total: 0,
  lastPage: 1,
  results: [],
};

const EMPTY_LIST_STATE: ListState = { page: EMPTY_PAGE, failed: false };

/**
 * Projects listing page. The listing is fetched server-side, keyed on the
 * active status filter and page; the banner shows per-status totals. Status
 * changes are persisted (optimistically, with rollback) and trigger a refetch.
 */
@Component({
  selector: 'app-projects-page',
  imports: [ProjectsOverviewBanner, FeedbackRoundListing, StatusFilter, Pagination, NewProjectDrawer],
  templateUrl: './projects-page.html',
  styleUrl: './projects-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsPage {
  private readonly feedbackRounds = inject(FeedbackRoundService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);

  /** True while any Feedback API request is in flight (drives the loading bar). */
  protected readonly loading = inject(FeedbackApiLoadingService).loading;

  /** New Project drawer visibility + in-flight create state. */
  protected readonly drawerOpen = signal(false);
  protected readonly creatingProject = signal(false);

  /**
   * Active status filter and current page (drive the server-side fetch). The page
   * opens on Open projects; the user can switch to All or another status.
   */
  protected readonly statusFilter = signal<StatusFilterValue>(FeedbackRoundStatus.Open);
  protected readonly page = signal(1);

  // Bumped after a status change to refetch the list + per-status totals.
  private readonly reloadVersion = signal(0);

  // The current page of rounds, refetched whenever the filter, page, or reload
  // version changes (server-side filtering + pagination).
  private readonly listState = toSignal(
    toObservable(
      computed(() => ({
        status: this.statusFilter(),
        page: this.page(),
        version: this.reloadVersion(),
      })),
    ).pipe(
      tap(() => this.listLoading.set(true)),
      switchMap(({ status, page }) =>
        this.feedbackRounds
          .listFeedbackRounds({ status: status === 'all' ? undefined : status, page })
          .pipe(
            map((roundsPage): ListState => ({ page: roundsPage, failed: false })),
            catchError(() => of<ListState>({ page: EMPTY_PAGE, failed: true })),
            tap(() => this.listLoading.set(false)),
          ),
      ),
    ),
    { initialValue: EMPTY_LIST_STATE },
  );

  /** True while a page of projects is being fetched; the listing shows placeholder rows meanwhile. */
  protected readonly listLoading = signal(true);

  private readonly listResult = computed(() => this.listState().page);

  // Per-status totals for the banner, refetched on reload.
  private readonly fetchedSummaries = toSignal(
    toObservable(this.reloadVersion).pipe(
      switchMap(() =>
        forkJoin(
          PROJECT_STATUS_TILES.map((tile) =>
            this.feedbackRounds.listFeedbackRounds({ status: tile.status }).pipe(
              map(
                (roundsPage): StatusSummary => ({
                  status: tile.status,
                  total: roundsPage.total,
                  orgIds: roundsPage.results.map((round) => round.orgId),
                  failed: false,
                }),
              ),
              catchError(() =>
                of<StatusSummary>({ status: tile.status, total: 0, orgIds: [], failed: true }),
              ),
            ),
          ),
        ),
      ),
    ),
    { initialValue: [] as StatusSummary[] },
  );

  // Locally-editable views seeded from the fetches, so a status change can be
  // applied optimistically and rolled back on failure.
  protected readonly rounds = linkedSignal(() => this.listResult().results);
  private readonly summaries = linkedSignal(() => this.fetchedSummaries());

  // Pagination state derived from the current page response.
  protected readonly currentPage = computed(() => this.listResult().page);
  protected readonly lastPage = computed(() => this.listResult().lastPage);
  protected readonly pageSize = computed(() => this.listResult().perPage);
  protected readonly filteredCount = computed(() => this.listResult().total);

  /**
   * True when the listing fetch failed. The per-status totals are deliberately
   * not consulted: they back the banner only, and one failing tile should not
   * replace a listing that loaded.
   */
  protected readonly loadFailed = computed(() => this.listState().failed);

  protected readonly statusCounts = computed<ProjectStatusCounts>(() => {
    const counts: ProjectStatusCounts = { ...EMPTY_STATUS_COUNTS };
    for (const summary of this.summaries()) {
      counts[summary.status] = summary.total;
    }
    return counts;
  });

  // Grand total across all statuses (the "of Y" in the banner subtitle).
  protected readonly total = computed(() =>
    this.summaries().reduce((runningTotal, summary) => runningTotal + summary.total, 0),
  );

  protected readonly organizationCount = computed(
    () => new Set(this.summaries().flatMap((summary) => summary.orgIds)).size,
  );

  constructor() {
    // If a refetch shrinks the result set below the current page, clamp back.
    effect(() => {
      const last = this.listResult().lastPage;
      if (untracked(this.page) > last && last >= 1) {
        this.page.set(last);
      }
    });
  }

  /** Re-runs the listing and the per-status totals after a failed load. */
  protected onRetryLoad(): void {
    this.reloadVersion.update((version) => version + 1);
  }

  protected onFilterChange(value: StatusFilterValue): void {
    this.statusFilter.set(value);
    this.page.set(1);
  }

  protected onStatusChange(change: RoundStatusChange): void {
    const previousStatus = change.round.status;
    const nextStatus = change.status;
    if (previousStatus === nextStatus) {
      return;
    }

    // Optimistically reflect the change, then persist it.
    this.applyStatusTransition(change.round.id, previousStatus, nextStatus);

    this.feedbackRounds
      .updateFeedbackRoundStatus(change.round.id, nextStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Reconcile with the server (filter membership, counts, pagination).
          this.reloadVersion.update((version) => version + 1);
        },
        error: () => {
          this.applyStatusTransition(change.round.id, nextStatus, previousStatus);
          this.snackBar.open('Could not update the project status. Reverted.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Moves a round between statuses in both the row list and the banner counts. */
  private applyStatusTransition(
    roundId: string,
    fromStatus: FeedbackRoundStatus,
    toStatus: FeedbackRoundStatus,
  ): void {
    this.rounds.update((rounds) =>
      rounds.map((round) => (round.id === roundId ? { ...round, status: toStatus } : round)),
    );
    this.summaries.update((summaries) =>
      summaries.map((summary) => {
        if (summary.status === fromStatus) {
          return { ...summary, total: Math.max(0, summary.total - 1) };
        }
        if (summary.status === toStatus) {
          return { ...summary, total: summary.total + 1 };
        }
        return summary;
      }),
    );
  }

  protected onOpenRound(roundId: string): void {
    this.router.navigate(['/projects', roundId]);
  }

  protected onNewProject(): void {
    this.drawerOpen.set(true);
  }

  /**
   * The signed-in user as a round creator — who the API makes the round's owner — or
   * undefined when the token has no email to attribute them by. A missing name falls
   * back to the address, so a nameless profile still ends up as the owner.
   */
  private roundCreator(): { name: string; email: string } | undefined {
    const author = this.currentUser.author();

    return author.email ? { name: author.name || author.email, email: author.email } : undefined;
  }

  protected onCreateProject(value: NewProjectFormValue): void {
    // The backend has no orgName field, so it's folded into projectName as
    // "Org Name - Project Name" (matching the existing naming convention).
    this.creatingProject.set(true);
    this.feedbackRounds
      .createFeedbackRound({
        orgId: value.orgId,
        projectId: value.atlasId,
        projectName: `${value.orgName} - ${value.projectName}`,
        // Seeds the creator as the round's first stakeholder, so its Project Team and
        // the comment composer's Notify picker are not empty before any feedback exists.
        // Omitted rather than half-sent when the token carries no name or email, since
        // the API requires both together.
        createdBy: this.roundCreator(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (round) => {
          this.creatingProject.set(false);
          this.drawerOpen.set(false);
          this.snackBar.open('Project created.', 'Dismiss', { duration: 4000 });
          // Navigate to the new project's detail page.
          this.router.navigate(['/projects', round.id]);
        },
        error: (error: { error?: { message?: string } }) => {
          this.creatingProject.set(false);
          this.snackBar.open(
            error?.error?.message ?? 'Could not create the project.',
            'Dismiss',
            { duration: 6000 },
          );
        },
      });
  }
}